import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CampaignRecipientFilter, RsvpResponseValue } from '@peles/shared';
import { EntityModal, FilterSelect } from '../../common';
import { useDialog } from '../../../context/DialogContext';
import { useEventGuestOptions } from '../../../hooks/useEventGuests';
import { useAddCampaignRecipients } from '../../../hooks/useInvites';
import { addCampaignRecipients } from '../../../api/invites';

export const RESPONSE_CHOICES: { id: RsvpResponseValue; label: string }[] = [
  { id: 'YES', label: 'Coming' },
  { id: 'MAYBE', label: 'Maybe' },
  { id: 'NO', label: 'Not coming' },
  { id: 'PENDING', label: 'No answer yet' },
];

export interface AddByFilterModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  campaignId: string;
}

function isEmpty(filter: CampaignRecipientFilter): boolean {
  return (
    !filter.sides?.length && !filter.tags?.length && !filter.responses?.length && !filter.notInvited
  );
}

/**
 * "Every Bride-side guest not yet invited": guests added by what they are
 * rather than one by one, with how many that is before anyone is added.
 */
export function AddByFilterModal({ open, onClose, eventId, campaignId }: AddByFilterModalProps) {
  const { alert } = useDialog();
  const { data: options } = useEventGuestOptions(open ? eventId : undefined);
  const add = useAddCampaignRecipients();
  const [filter, setFilter] = useState<CampaignRecipientFilter>({ notInvited: true });

  useEffect(() => {
    if (open) setFilter({ notInvited: true });
  }, [open]);

  const { data: preview, isFetching } = useQuery({
    queryKey: ['invite-recipients', campaignId, 'filter-preview', filter],
    queryFn: () => addCampaignRecipients(campaignId, { filter, dryRun: true }),
    enabled: open && !isEmpty(filter),
  });
  const count = isEmpty(filter) ? 0 : (preview?.added ?? 0);

  const handleAdd = async () => {
    try {
      const result = await add.mutateAsync({ campaignId, input: { filter } });
      onClose();
      await alert({
        message: `Added ${result.added} guest${result.added === 1 ? '' : 's'} to the campaign.`,
        variant: 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Could not add the guests',
        variant: 'error',
      });
    }
  };

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Add guests by filter"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={count === 0 || isFetching || add.isPending}
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {add.isPending
              ? 'Adding…'
              : count === 0
                ? 'Nobody to add'
                : `Add ${count} guest${count === 1 ? '' : 's'}`}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Guests matching every choice below are added. Anyone already on this campaign is left out.
        </p>
        <div className="flex flex-wrap gap-2">
          <FilterSelect
            label="Side"
            multiple
            items={options?.sides ?? []}
            value={filter.sides ?? []}
            onChange={(v) => setFilter((f) => ({ ...f, sides: v as string[] }))}
            getValue={(o) => o.value}
            getLabel={(o) => o.label}
            emptyMessage="No sides in Settings"
          />
          <FilterSelect
            label="Tags"
            multiple
            searchable
            items={options?.tags ?? []}
            value={filter.tags ?? []}
            onChange={(v) => setFilter((f) => ({ ...f, tags: v as string[] }))}
            getValue={(o) => o.value}
            getLabel={(o) => o.label}
            emptyMessage="No tags in Settings"
          />
          <FilterSelect
            label="Answer"
            multiple
            items={RESPONSE_CHOICES}
            value={filter.responses ?? []}
            onChange={(v) => setFilter((f) => ({ ...f, responses: v as RsvpResponseValue[] }))}
            getValue={(o) => o.id}
            getLabel={(o) => o.label}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filter.notInvited === true}
            onChange={(e) => setFilter((f) => ({ ...f, notInvited: e.target.checked }))}
          />
          Only guests not on any campaign of this event yet
        </label>
        <p className="text-sm font-medium text-gray-900" aria-live="polite">
          {isEmpty(filter)
            ? 'Choose who to add.'
            : isFetching
              ? 'Counting…'
              : `${count} guest${count === 1 ? '' : 's'} match.`}
        </p>
      </div>
    </EntityModal>
  );
}
